/**
 * RolesKnowledgeAccess — Settings → Users & Roles → Knowledge access (mockups/kiaan-knowledge.html, screen 4).
 * Rows = containers (branches open below them), columns = roles. Click a container cell to cycle
 * all → part → none: "part" opens the branches so you pick them one by one (nothing is written
 * until you click a branch); "none" and "all" write the container row and clear the branch rows.
 * New containers start as Admin only (the server does that). Only kiaan_manage_knowledge admins see
 * this; the server refuses everyone else too.
 */
import './knowledge.css';
import { useEffect, useState } from 'react';
import { useKiaanAccess } from '../useKiaanAccess';
import { getRoleAccess, knowledgeErrorText, knowledgeLoadErrorText, setGrant, type AccessState, type RoleAccess, type RoleAccessGroup } from './knowledgeApi';

const cellKey = (g: string, r: string) => `${g}\u0000${r}`;
const LABEL: Record<AccessState, string> = { on: 'all', partial: 'part', off: '—' };
const CLS: Record<AccessState, string> = { on: 'kn-y', partial: 'kn-p', off: 'kn-n' };

export function RolesKnowledgeAccess() {
  const [data, setData] = useState<RoleAccess | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [part, setPart] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    getRoleAccess()
      .then((d) => {
        if (!alive) return;
        setData(d);
        setError(null);
        // A container the server now calls "part" no longer needs the local "picking" mark.
        setPart((p) => new Set([...p].filter((k) => { const [g, r] = k.split('\u0000'); return d.groups.find((x) => x.key === g)?.cells[r]?.state !== 'partial'; })));
      })
      .catch((e) => { if (alive) setError(knowledgeLoadErrorText(e)); });
    return () => { alive = false; };
  }, [tick]);

  if (!data) return error ? <div className="kw-banner" role="status">{error}</div> : <div className="kw-small">Loading…</div>;

  const branchesOf = (key: string) => data.groups.filter((g) => g.parentKey === key);
  const containers = data.groups.filter((g) => !g.parentKey);

  const write = async (steps: [string, boolean | null][], role: string) => {
    setBusy(true);
    setError(null);
    try {
      for (const [g, allow] of steps) await setGrant(g, 'role', role, allow);
      setTick((t) => t + 1);
    } catch (e) {
      setError(knowledgeErrorText(e));
    } finally {
      setBusy(false);
    }
  };

  const clickContainer = (g: RoleAccessGroup, role: string) => {
    const kids = branchesOf(g.key);
    const k = cellKey(g.key, role);
    const shown: AccessState = part.has(k) ? 'partial' : g.cells[role]?.state ?? 'off';
    const clear: [string, boolean | null][] = kids.filter((b) => b.cells[role]?.own != null).map((b) => [b.key, null]);
    if (kids.length && shown === 'on') {
      setPart((p) => new Set(p).add(k));
      setOpen((o) => new Set(o).add(g.key));
      return;
    }
    setPart((p) => { const n = new Set(p); n.delete(k); return n; });
    void write([[g.key, shown === 'off'], ...clear], role);
  };

  const clickBranch = (b: RoleAccessGroup, role: string) => {
    const on = (b.cells[role]?.state ?? 'off') === 'on';
    void write([[b.key, !on]], role);
  };

  return (
    <div className="kn-roles" data-testid="roles-knowledge-access">
      {error && <div className="kw-banner kw-danger" role="alert">{error}</div>}
      {containers.length === 0 ? (
        <div className="kw-small">No knowledge yet. Add a folder in Kiaan → Dev mode → Knowledge.</div>
      ) : (
        <table className="kn-table">
          <thead>
            <tr><th>Container › branch</th>{data.roles.map((r) => <th key={r} style={{ textAlign: 'center' }}>{r}</th>)}</tr>
          </thead>
          <tbody>
            {containers.map((g) => {
              const kids = branchesOf(g.key);
              const isOpen = open.has(g.key);
              return [
                <tr key={g.key}>
                  <td>
                    {kids.length > 0 && (
                      <button type="button" className="kn-tog" aria-label={`${isOpen ? 'Close' : 'Open'} ${g.label}`} onClick={() => setOpen((o) => { const n = new Set(o); if (n.has(g.key)) n.delete(g.key); else n.add(g.key); return n; })}>{isOpen ? '▾' : '▸'}</button>
                    )}
                    <b>{g.label}</b>
                  </td>
                  {data.roles.map((r) => {
                    const st: AccessState = part.has(cellKey(g.key, r)) ? 'partial' : g.cells[r]?.state ?? 'off';
                    const label = kids.length ? LABEL[st] : st === 'on' ? '✓' : '—';
                    return (
                      <td key={r} className="kn-c">
                        <button type="button" className={`kn-cell ${CLS[st]}`} disabled={busy} aria-label={`${g.label} for ${r}: ${label}`} onClick={() => clickContainer(g, r)}>{label}</button>
                      </td>
                    );
                  })}
                </tr>,
                ...(isOpen ? kids.map((b) => (
                  <tr key={b.key}>
                    <td style={{ paddingLeft: 26 }}>{b.label}</td>
                    {data.roles.map((r) => {
                      const st = b.cells[r]?.state ?? 'off';
                      return (
                        <td key={r} className="kn-c">
                          <button type="button" className={`kn-cell ${CLS[st]}`} disabled={busy} aria-label={`${b.label} for ${r}: ${st === 'on' ? '✓' : '—'}`} onClick={() => clickBranch(b, r)}>{st === 'on' ? '✓' : '—'}</button>
                        </td>
                      );
                    })}
                  </tr>
                )) : []),
              ];
            })}
          </tbody>
        </table>
      )}
      <div className="kw-small" style={{ marginTop: 6 }}>Click a cell to switch. "part" opens the branches. New containers start as Admin only.</div>
    </div>
  );
}

/** The Settings block: only for admins who manage knowledge (fails closed while permissions load). */
export function KnowledgeAccessSection() {
  const { manageKnowledge } = useKiaanAccess();
  if (!manageKnowledge) return null;
  return (
    <section className="kw-root-inline mt-8 rounded-xl border border-gray-200 bg-white p-4" aria-label="Knowledge access">
      <h3 className="text-base font-semibold text-gray-900">Knowledge access</h3>
      <p className="mb-3 text-sm text-gray-500">Which roles may read each knowledge container. Kiaan's agents never read more than the person who asks them.</p>
      <RolesKnowledgeAccess />
    </section>
  );
}
